import { useState, useEffect, useCallback, useRef } from 'react';
import { Layout } from '../components/Layout';
import {
  getParameterCodes,
  getParameterTrend,
  getParameterSummary,
  getOutOfRangeParameters,
  getLatestParameters,
  getTestDates,
  getParametersByDate,
  exportParameters,
  exportParametersPdf,
  formatDate,
  type ParameterCode,
  type TrendData,
  type ParameterSummary,
  type LabParameter,
} from '../api/parameters';

type TabType = 'parametri' | 'perData' | 'confronta';

// Colors for comparison chart lines
const COMPARISON_COLORS = [
  '#3B82F6', // blue
  '#10B981', // green
  '#F59E0B', // amber
  '#EF4444', // red
  '#8B5CF6', // purple
  '#EC4899', // pink
  '#06B6D4', // cyan
  '#F97316', // orange
];

export function Parameters() {
  const [codes, setCodes] = useState<ParameterCode[]>([]);
  const [summary, setSummary] = useState<ParameterSummary | null>(null);
  const [outOfRange, setOutOfRange] = useState<Array<LabParameter & { history: Array<{ value: number; testDate: string; isWithinRange: boolean }> }>>([]);
  const [latestBySection, setLatestBySection] = useState<Record<string, LabParameter[]>>({});
  const [selectedCode, setSelectedCode] = useState<string | null>(null);
  const [trendData, setTrendData] = useState<TrendData | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingTrend, setLoadingTrend] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState<TabType>('parametri');
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set());

  // Per Data tab state
  const [testDates, setTestDates] = useState<Array<{ date: string; count: number }>>([]);
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [dateFilterFrom, setDateFilterFrom] = useState('');
  const [dateFilterTo, setDateFilterTo] = useState('');
  const [dateParameters, setDateParameters] = useState<LabParameter[]>([]);
  const [dateParametersBySection, setDateParametersBySection] = useState<Record<string, LabParameter[]>>({});
  const [loadingDateParams, setLoadingDateParams] = useState(false);

  // Confronta tab state
  const [comparisonCodes, setComparisonCodes] = useState<Set<string>>(new Set());
  const [comparisonTrends, setComparisonTrends] = useState<TrendData[]>([]);

  // Export dropdown state
  const [exportDropdownOpen, setExportDropdownOpen] = useState(false);

  const loadData = useCallback(async () => {
    try {
      const [codesRes, summaryRes, outOfRangeRes, latestRes, datesRes] = await Promise.all([
        getParameterCodes(),
        getParameterSummary(),
        getOutOfRangeParameters(),
        getLatestParameters(),
        getTestDates(),
      ]);
      setCodes(codesRes.codes);
      setSummary(summaryRes.summary);
      setOutOfRange(outOfRangeRes.parameters);
      setLatestBySection(latestRes.bySection || {});
      setTestDates(datesRes.dates);
      // All sections collapsed by default
    } catch (error) {
      console.error('Error loading parameters:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const loadTrend = async (code: string) => {
    setSelectedCode(code);
    setLoadingTrend(true);
    try {
      const res = await getParameterTrend(code);
      setTrendData(res);
    } catch (error) {
      console.error('Error loading trend:', error);
    } finally {
      setLoadingTrend(false);
    }
  };

  const toggleSection = (section: string) => {
    setExpandedSections(prev => {
      const newSet = new Set(prev);
      if (newSet.has(section)) {
        newSet.delete(section);
      } else {
        newSet.add(section);
      }
      return newSet;
    });
  };

  // Load parameters for selected date
  const loadDateParameters = async (date: string) => {
    setSelectedDate(date);
    setLoadingDateParams(true);
    try {
      const res = await getParametersByDate(date);
      setDateParameters(res.parameters);
      setDateParametersBySection(res.bySection);
    } catch (error) {
      console.error('Error loading date parameters:', error);
    } finally {
      setLoadingDateParams(false);
    }
  };

  // Toggle parameter in comparison
  const toggleComparison = async (code: string) => {
    const newCodes = new Set(comparisonCodes);
    if (newCodes.has(code)) {
      newCodes.delete(code);
      setComparisonTrends(prev => prev.filter(t => t.code !== code));
    } else {
      newCodes.add(code);
      // Load trend for new code
      try {
        const trend = await getParameterTrend(code);
        setComparisonTrends(prev => [...prev, trend]);
      } catch (error) {
        console.error('Error loading trend for comparison:', error);
      }
    }
    setComparisonCodes(newCodes);
  };

  const clearComparison = () => {
    setComparisonCodes(new Set());
    setComparisonTrends([]);
  };

  // Group codes by section
  const groupedBySection = codes.reduce((acc, code) => {
    let section = 'ALTRI';
    for (const [sectionName, params] of Object.entries(latestBySection)) {
      if (params.some(p => p.parameterCode === code.code)) {
        section = sectionName;
        break;
      }
    }
    if (!acc[section]) {
      acc[section] = [];
    }
    acc[section].push(code);
    return acc;
  }, {} as Record<string, ParameterCode[]>);

  const expandAllSections = () => {
    setExpandedSections(new Set(Object.keys(groupedBySection)));
  };

  const collapseAllSections = () => {
    setExpandedSections(new Set());
  };

  // Filter codes based on search
  const filterCodes = (codes: ParameterCode[]) => {
    if (!searchTerm) return codes;
    return codes.filter(
      c =>
        c.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        c.code.toLowerCase().includes(searchTerm.toLowerCase())
    );
  };

  // Filter dates based on date filters
  const filteredDates = testDates.filter(d => {
    if (dateFilterFrom && d.date < dateFilterFrom) return false;
    if (dateFilterTo && d.date > dateFilterTo) return false;
    return true;
  });

  // Handle export CSV
  const handleExportCsv = async () => {
    try {
      // If in Per Data tab with a selected date, export only that date
      const options = activeTab === 'perData' && selectedDate
        ? { from: selectedDate, to: selectedDate }
        : undefined;
      await exportParameters(options);
      setExportDropdownOpen(false);
    } catch (error) {
      console.error('Error exporting CSV:', error);
    }
  };

  // Handle export PDF
  const handleExportPdf = async () => {
    try {
      // If in Per Data tab with a selected date, export only that date
      const options = activeTab === 'perData' && selectedDate
        ? { from: selectedDate, to: selectedDate }
        : undefined;
      await exportParametersPdf(options);
      setExportDropdownOpen(false);
    } catch (error) {
      console.error('Error exporting PDF:', error);
    }
  };

  // Handle export (for backward compatibility with TrendPanel)
  const handleExport = handleExportCsv;

  // Handle copy to clipboard
  const handleCopy = async () => {
    if (!trendData) return;
    const text = trendData.data
      .map(d => `${formatDate(d.testDate)}\t${d.value}\t${d.unit}\t${d.referenceMin ?? '-'} - ${d.referenceMax ?? '-'}`)
      .join('\n');
    const header = `Data\tValore\tUnità\tRange\n`;
    await navigator.clipboard.writeText(header + text);
  };

  if (loading) {
    return (
      <Layout title="Parametri Laboratorio">
        <div className="flex items-center justify-center h-64">
          <div className="text-lg lg:text-xl text-gray-500">Caricamento...</div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout title="Parametri Laboratorio">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex justify-between items-start mb-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center">
              <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Parametri Laboratorio</h1>
              <p className="text-gray-500">{summary?.totalParameters || 0} parametri monitorati</p>
            </div>
          </div>
          <div className="relative">
            <button
              onClick={() => setExportDropdownOpen(!exportDropdownOpen)}
              className="flex items-center gap-2 px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-xl transition-colors"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
              </svg>
              Esporta
              <svg className={`w-4 h-4 transition-transform ${exportDropdownOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            {exportDropdownOpen && (
              <>
                <div
                  className="fixed inset-0 z-10"
                  onClick={() => setExportDropdownOpen(false)}
                />
                <div className="absolute right-0 top-full mt-1 bg-white border border-gray-200 rounded-xl shadow-lg z-20 min-w-[200px]">
                  <button
                    onClick={handleExportCsv}
                    className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-gray-50 transition-colors rounded-t-xl"
                  >
                    <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    <span className="text-gray-700">Esporta tutti (CSV)</span>
                  </button>
                  <button
                    onClick={handleExportPdf}
                    className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-gray-50 transition-colors rounded-b-xl border-t border-gray-100"
                  >
                    <svg className="w-5 h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                    </svg>
                    <span className="text-gray-700">Esporta tutti (PDF)</span>
                  </button>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6">
          <button
            onClick={() => setActiveTab('parametri')}
            className={`px-4 py-2 rounded-xl text-sm font-medium transition-colors ${
              activeTab === 'parametri'
                ? 'bg-blue-600 text-white'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            Parametri
          </button>
          <button
            onClick={() => setActiveTab('perData')}
            className={`px-4 py-2 rounded-xl text-sm font-medium transition-colors ${
              activeTab === 'perData'
                ? 'bg-blue-600 text-white'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            Per Data
          </button>
          <button
            onClick={() => setActiveTab('confronta')}
            className={`px-4 py-2 rounded-xl text-sm font-medium transition-colors flex items-center gap-2 ${
              activeTab === 'confronta'
                ? 'bg-blue-600 text-white'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
            </svg>
            Confronta
            {comparisonCodes.size > 0 && (
              <span className="bg-white text-blue-600 text-xs font-bold px-2 py-0.5 rounded-full">
                {comparisonCodes.size}
              </span>
            )}
          </button>
        </div>

        {/* Tab Content */}
        {activeTab === 'parametri' && (
          <ParametriTab
            groupedBySection={groupedBySection}
            expandedSections={expandedSections}
            toggleSection={toggleSection}
            expandAllSections={expandAllSections}
            collapseAllSections={collapseAllSections}
            filterCodes={filterCodes}
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            selectedCode={selectedCode}
            loadTrend={loadTrend}
            trendData={trendData}
            loadingTrend={loadingTrend}
            outOfRange={outOfRange}
            summary={summary}
            handleCopy={handleCopy}
            handleExport={handleExport}
            formatDate={formatDate}
          />
        )}

        {activeTab === 'perData' && (
          <PerDataTab
            testDates={filteredDates}
            selectedDate={selectedDate}
            loadDateParameters={loadDateParameters}
            dateFilterFrom={dateFilterFrom}
            setDateFilterFrom={setDateFilterFrom}
            dateFilterTo={dateFilterTo}
            setDateFilterTo={setDateFilterTo}
            dateParameters={dateParameters}
            dateParametersBySection={dateParametersBySection}
            loadingDateParams={loadingDateParams}
            formatDate={formatDate}
          />
        )}

        {activeTab === 'confronta' && (
          <ConfrontaTab
            groupedBySection={groupedBySection}
            expandedSections={expandedSections}
            toggleSection={toggleSection}
            filterCodes={filterCodes}
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            comparisonCodes={comparisonCodes}
            toggleComparison={toggleComparison}
            clearComparison={clearComparison}
            comparisonTrends={comparisonTrends}
            selectedCode={selectedCode}
            loadTrend={loadTrend}
            trendData={trendData}
            loadingTrend={loadingTrend}
            handleCopy={handleCopy}
            formatDate={formatDate}
          />
        )}
      </div>
    </Layout>
  );
}

// Parametri Tab Component
function ParametriTab({
  groupedBySection,
  expandedSections,
  toggleSection,
  expandAllSections,
  collapseAllSections,
  filterCodes,
  searchTerm,
  setSearchTerm,
  selectedCode,
  loadTrend,
  trendData,
  loadingTrend,
  outOfRange,
  summary,
  handleCopy,
  handleExport,
  formatDate,
}: {
  groupedBySection: Record<string, ParameterCode[]>;
  expandedSections: Set<string>;
  toggleSection: (section: string) => void;
  expandAllSections: () => void;
  collapseAllSections: () => void;
  filterCodes: (codes: ParameterCode[]) => ParameterCode[];
  searchTerm: string;
  setSearchTerm: (term: string) => void;
  selectedCode: string | null;
  loadTrend: (code: string) => void;
  trendData: TrendData | null;
  loadingTrend: boolean;
  outOfRange: Array<LabParameter & { history: Array<{ value: number; testDate: string; isWithinRange: boolean }> }>;
  summary: ParameterSummary | null;
  handleCopy: () => void;
  handleExport: () => void;
  formatDate: (date: string) => string;
}) {
  return (
    <div className="flex gap-6">
      {/* Left Panel - Parameter List */}
      <div className="w-96 flex-shrink-0">
        {/* Search */}
        <div className="flex gap-2 mb-4">
          <div className="flex-1 relative">
            <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            <input
              type="text"
              placeholder="Cerca parametro..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          <button className="p-2.5 border border-gray-200 rounded-xl hover:bg-gray-50 transition-colors">
            <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
            </svg>
          </button>
        </div>

        {/* Parameters by Section */}
        <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
          <div className="px-4 py-3 bg-gray-50 border-b border-gray-200 flex items-center justify-between">
            <span className="font-medium text-gray-700">Parametri ({summary?.uniqueParameterTypes || 0})</span>
            <div className="flex items-center gap-1">
              <button
                onClick={expandAllSections}
                className="p-1.5 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors"
                title="Espandi tutti"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
                </svg>
              </button>
              <button
                onClick={collapseAllSections}
                className="p-1.5 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors"
                title="Comprimi tutti"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 9V4.5M9 9H4.5M9 9L3.75 3.75M9 15v4.5M9 15H4.5M9 15l-5.25 5.25M15 9h4.5M15 9V4.5M15 9l5.25-5.25M15 15h4.5M15 15v4.5m0-4.5l5.25 5.25" />
                </svg>
              </button>
            </div>
          </div>

          <div className="max-h-[400px] overflow-y-auto">
            {Object.entries(groupedBySection).map(([section, sectionCodes]) => {
              const filteredSectionCodes = filterCodes(sectionCodes);
              if (filteredSectionCodes.length === 0 && searchTerm) return null;

              const isExpanded = expandedSections.has(section);
              const displayCodes = searchTerm ? filteredSectionCodes : (isExpanded ? sectionCodes : []);

              return (
                <div key={section}>
                  <button
                    onClick={() => toggleSection(section)}
                    className="w-full flex items-center justify-between px-4 py-3 hover:bg-gray-50 transition-colors border-b border-gray-100"
                  >
                    <span className="text-sm font-semibold text-blue-600 uppercase">{section}</span>
                    <div className="flex items-center gap-2">
                      <span className="bg-gray-100 text-gray-600 text-xs font-medium px-2 py-1 rounded-full">
                        {sectionCodes.length}
                      </span>
                      <svg
                        className={`w-4 h-4 text-gray-400 transition-transform ${isExpanded ? 'rotate-90' : ''}`}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </div>
                  </button>

                  {(isExpanded || searchTerm) && displayCodes.map(code => (
                    <button
                      key={code.code}
                      onClick={() => loadTrend(code.code)}
                      className={`w-full text-left px-4 py-3 hover:bg-gray-50 transition-colors border-b border-gray-100 ${
                        selectedCode === code.code ? 'bg-blue-50 border-l-4 border-l-blue-500' : ''
                      }`}
                    >
                      <div className="flex justify-between items-start">
                        <div>
                          <p className="font-medium text-gray-900">{code.name}</p>
                          {code.latest && (
                            <p className="text-sm text-gray-500">
                              Ultimo: {code.latest.value.toFixed(2)} {code.latest.unit}
                            </p>
                          )}
                        </div>
                        <span className="bg-gray-100 text-gray-600 text-xs font-medium px-2 py-1 rounded-full">
                          {code.count}
                        </span>
                      </div>
                    </button>
                  ))}
                </div>
              );
            })}
          </div>
        </div>

        {/* Valori Anomali Section */}
        {outOfRange.length > 0 && (
          <div className="mt-4 bg-red-50 rounded-2xl border border-red-200 overflow-hidden">
            <div className="px-4 py-3 flex items-center gap-2 border-b border-red-200">
              <svg className="w-5 h-5 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
              <span className="font-semibold text-red-700">Valori Anomali</span>
              <span className="bg-red-200 text-red-700 text-xs font-medium px-2 py-1 rounded-full">
                {outOfRange.length}
              </span>
            </div>
            <div className="max-h-[200px] overflow-y-auto">
              {outOfRange.map(param => (
                <button
                  key={param.id}
                  onClick={() => loadTrend(param.parameterCode)}
                  className="w-full text-left px-4 py-3 hover:bg-red-100 transition-colors border-b border-red-100 last:border-b-0"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-red-200 rounded-full flex items-center justify-center">
                      <svg className="w-4 h-4 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01" />
                      </svg>
                    </div>
                    <div className="flex-1">
                      <p className="font-medium text-gray-900">{param.parameterName}</p>
                      <p className="text-xs text-gray-500">{formatDate(param.testDate)}</p>
                    </div>
                    <div className="text-right">
                      <p className="font-bold text-red-600">
                        {param.value} <span className="font-normal text-sm">{param.unit}</span>
                      </p>
                      <p className="text-xs text-gray-500">
                        Range: {param.referenceMin ?? 0} - {param.referenceMax ?? '∞'}
                      </p>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Right Panel - Trend Chart */}
      <div className="flex-1">
        <TrendPanel
          selectedCode={selectedCode}
          trendData={trendData}
          loadingTrend={loadingTrend}
          handleCopy={handleCopy}
          handleExport={handleExport}
          formatDate={formatDate}
        />
      </div>
    </div>
  );
}

// Per Data Tab Component
function PerDataTab({
  testDates,
  selectedDate,
  loadDateParameters,
  dateFilterFrom,
  setDateFilterFrom,
  dateFilterTo,
  setDateFilterTo,
  dateParameters,
  dateParametersBySection,
  loadingDateParams,
  formatDate,
}: {
  testDates: Array<{ date: string; count: number }>;
  selectedDate: string | null;
  loadDateParameters: (date: string) => void;
  dateFilterFrom: string;
  setDateFilterFrom: (date: string) => void;
  dateFilterTo: string;
  setDateFilterTo: (date: string) => void;
  dateParameters: LabParameter[];
  dateParametersBySection: Record<string, LabParameter[]>;
  loadingDateParams: boolean;
  formatDate: (date: string) => string;
}) {
  return (
    <div className="flex gap-6">
      {/* Left Panel - Date List */}
      <div className="w-96 flex-shrink-0">
        {/* Date Filters */}
        <div className="bg-white rounded-2xl border border-gray-200 p-4 mb-4">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
              </svg>
              <span className="font-medium text-gray-700">Filtri Data</span>
            </div>
            {(dateFilterFrom || dateFilterTo) && (
              <button
                onClick={() => {
                  setDateFilterFrom('');
                  setDateFilterTo('');
                }}
                className="text-xs text-gray-500 hover:text-red-600 transition-colors flex items-center gap-1"
                title="Azzera filtri"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
                Azzera
              </button>
            )}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs text-gray-500 mb-1">Da</label>
              <ItalianDateInput
                value={dateFilterFrom}
                onChange={setDateFilterFrom}
                placeholder="gg/mm/aaaa"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">A</label>
              <ItalianDateInput
                value={dateFilterTo}
                onChange={setDateFilterTo}
                placeholder="gg/mm/aaaa"
              />
            </div>
          </div>
        </div>

        {/* Date List */}
        <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
          <div className="px-4 py-3 bg-gray-50 border-b border-gray-200 flex items-center gap-2">
            <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
            <span className="font-medium text-gray-700">Date Esami ({testDates.length})</span>
          </div>

          <div className="max-h-[500px] overflow-y-auto">
            {testDates.map(({ date, count }) => (
              <button
                key={date}
                onClick={() => loadDateParameters(date)}
                className={`w-full text-left px-4 py-3 hover:bg-gray-50 transition-colors border-b border-gray-100 flex justify-between items-center ${
                  selectedDate === date ? 'bg-blue-50 border-l-4 border-l-blue-500' : ''
                }`}
              >
                <span className="font-medium text-gray-900">{formatDate(date)}</span>
                <span className="bg-gray-100 text-gray-600 text-xs font-medium px-2 py-1 rounded-full">
                  {count} parametri
                </span>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Right Panel - Parameters for Date */}
      <div className="flex-1">
        {selectedDate ? (
          loadingDateParams ? (
            <div className="bg-white rounded-2xl border border-gray-200 p-8 flex items-center justify-center h-96">
              <p className="text-lg text-gray-500">Caricamento...</p>
            </div>
          ) : (
            <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-200">
                <h2 className="text-xl font-bold text-gray-900">
                  Parametri del {formatDate(selectedDate)}
                </h2>
                <p className="text-gray-500">{dateParameters.length} parametri analizzati</p>
              </div>

              <div className="max-h-[600px] overflow-y-auto">
                {Object.entries(dateParametersBySection).map(([section, params]) => (
                  <div key={section}>
                    <div className="px-6 py-3 bg-gray-50 border-b border-gray-200">
                      <span className="text-sm font-semibold text-blue-600 uppercase">{section}</span>
                    </div>
                    <table className="w-full">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-2 text-left text-xs font-semibold text-gray-600">Parametro</th>
                          <th className="px-6 py-2 text-left text-xs font-semibold text-gray-600">Valore</th>
                          <th className="px-6 py-2 text-left text-xs font-semibold text-gray-600">Range</th>
                          <th className="px-6 py-2 text-left text-xs font-semibold text-gray-600">Stato</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {params.map(param => (
                          <tr key={param.id} className="hover:bg-gray-50">
                            <td className="px-6 py-3">
                              <p className="font-medium text-gray-900">{param.parameterName}</p>
                              <p className="text-xs text-gray-500">{param.parameterCode}</p>
                            </td>
                            <td className="px-6 py-3">
                              <span className={param.isWithinRange ? 'text-gray-900' : 'text-red-600 font-bold'}>
                                {param.value.toFixed(2)} {param.unit}
                              </span>
                            </td>
                            <td className="px-6 py-3 text-gray-500">
                              {param.referenceMin ?? '-'} - {param.referenceMax ?? '-'}
                            </td>
                            <td className="px-6 py-3">
                              <ValueRangeBar
                                value={param.value}
                                min={param.referenceMin}
                                max={param.referenceMax}
                                isWithinRange={param.isWithinRange}
                              />
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ))}
              </div>
            </div>
          )
        ) : (
          <div className="bg-white rounded-2xl border border-gray-200 p-8 flex items-center justify-center h-96">
            <div className="text-center">
              <svg className="w-16 h-16 mx-auto text-gray-300 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              <p className="text-lg text-gray-500">Seleziona una data</p>
              <p className="text-sm text-gray-400 mt-1">
                Scegli una data esame dalla lista a sinistra per visualizzare tutti i parametri analizzati in quella data.
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// Confronta Tab Component
function ConfrontaTab({
  groupedBySection,
  expandedSections,
  toggleSection,
  filterCodes,
  searchTerm,
  setSearchTerm,
  comparisonCodes,
  toggleComparison,
  clearComparison,
  comparisonTrends,
  selectedCode,
  loadTrend,
  trendData,
  loadingTrend,
  handleCopy,
  formatDate,
}: {
  groupedBySection: Record<string, ParameterCode[]>;
  expandedSections: Set<string>;
  toggleSection: (section: string) => void;
  filterCodes: (codes: ParameterCode[]) => ParameterCode[];
  searchTerm: string;
  setSearchTerm: (term: string) => void;
  comparisonCodes: Set<string>;
  toggleComparison: (code: string) => void;
  clearComparison: () => void;
  comparisonTrends: TrendData[];
  selectedCode: string | null;
  loadTrend: (code: string) => void;
  trendData: TrendData | null;
  loadingTrend: boolean;
  handleCopy: () => void;
  formatDate: (date: string) => string;
}) {
  return (
    <div className="flex gap-6">
      {/* Left Panel - Parameter List with toggle */}
      <div className="w-96 flex-shrink-0">
        {/* Search */}
        <div className="flex gap-2 mb-4">
          <div className="flex-1 relative">
            <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            <input
              type="text"
              placeholder="Cerca parametro..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          <button className="p-2.5 border border-gray-200 rounded-xl hover:bg-gray-50 transition-colors">
            <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" />
            </svg>
          </button>
        </div>

        {/* Parameters by Section */}
        <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
          <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
            <span className="font-medium text-gray-700">Parametri ({Object.values(groupedBySection).flat().length})</span>
          </div>

          <div className="max-h-[500px] overflow-y-auto">
            {Object.entries(groupedBySection).map(([section, sectionCodes]) => {
              const filteredSectionCodes = filterCodes(sectionCodes);
              if (filteredSectionCodes.length === 0 && searchTerm) return null;

              const isExpanded = expandedSections.has(section);
              const displayCodes = searchTerm ? filteredSectionCodes : (isExpanded ? sectionCodes : []);

              return (
                <div key={section}>
                  <button
                    onClick={() => toggleSection(section)}
                    className="w-full flex items-center justify-between px-4 py-3 hover:bg-gray-50 transition-colors border-b border-gray-100"
                  >
                    <span className="text-sm font-semibold text-blue-600 uppercase">{section}</span>
                    <div className="flex items-center gap-2">
                      <span className="bg-gray-100 text-gray-600 text-xs font-medium px-2 py-1 rounded-full">
                        {sectionCodes.length}
                      </span>
                      <svg
                        className={`w-4 h-4 text-gray-400 transition-transform ${isExpanded ? 'rotate-90' : ''}`}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </div>
                  </button>

                  {(isExpanded || searchTerm) && displayCodes.map(code => {
                    const isSelected = comparisonCodes.has(code.code);
                    return (
                      <div
                        key={code.code}
                        className={`flex items-center px-4 py-3 border-b border-gray-100 ${
                          selectedCode === code.code ? 'bg-blue-50' : ''
                        }`}
                      >
                        <button
                          onClick={() => loadTrend(code.code)}
                          className="flex-1 text-left hover:bg-gray-50 transition-colors"
                        >
                          <p className="font-medium text-gray-900">{code.name}</p>
                          {code.latest && (
                            <p className="text-sm text-gray-500">
                              Ultimo: {code.latest.value.toFixed(2)} {code.latest.unit}
                            </p>
                          )}
                        </button>
                        <div className="flex items-center gap-2">
                          <span className="bg-gray-100 text-gray-600 text-xs font-medium px-2 py-1 rounded-full">
                            {code.count}
                          </span>
                          <button
                            onClick={() => toggleComparison(code.code)}
                            className={`p-1.5 rounded-lg transition-colors ${
                              isSelected
                                ? 'bg-red-100 text-red-600 hover:bg-red-200'
                                : 'bg-blue-100 text-blue-600 hover:bg-blue-200'
                            }`}
                          >
                            {isSelected ? (
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                              </svg>
                            ) : (
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                              </svg>
                            )}
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Right Panel - Trend and Comparison */}
      <div className="flex-1 space-y-6">
        <TrendPanel
          selectedCode={selectedCode}
          trendData={trendData}
          loadingTrend={loadingTrend}
          handleCopy={handleCopy}
          handleExport={() => {}}
          formatDate={formatDate}
        />

        {/* Comparison Section */}
        {comparisonTrends.length > 0 && (
          <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
              <div className="flex items-center gap-3">
                <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
                <h2 className="text-xl font-bold text-gray-900">Confronto Parametri</h2>
              </div>
              <button
                onClick={clearComparison}
                className="text-sm text-gray-500 hover:text-gray-700"
              >
                Pulisci tutto
              </button>
            </div>

            {/* Parameter Chips */}
            <div className="px-6 py-4 flex flex-wrap gap-2 border-b border-gray-100">
              {comparisonTrends.map((trend, index) => (
                <span
                  key={trend.code}
                  className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium"
                  style={{
                    backgroundColor: `${COMPARISON_COLORS[index % COMPARISON_COLORS.length]}20`,
                    color: COMPARISON_COLORS[index % COMPARISON_COLORS.length]
                  }}
                >
                  <span
                    className="w-2 h-2 rounded-full"
                    style={{ backgroundColor: COMPARISON_COLORS[index % COMPARISON_COLORS.length] }}
                  />
                  {trend.name} ({trend.code}) ({trend.data.length})
                  <button
                    onClick={() => toggleComparison(trend.code)}
                    className="ml-1 hover:opacity-70"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </span>
              ))}
            </div>

            {/* Comparison Chart */}
            <div className="p-6">
              <ComparisonChart trends={comparisonTrends} formatDate={formatDate} />
            </div>

            {/* Legend */}
            <div className="px-6 pb-4 flex flex-wrap justify-center gap-6">
              {comparisonTrends.map((trend, index) => (
                <div key={trend.code} className="flex items-center gap-2">
                  <span
                    className="w-3 h-0.5"
                    style={{ backgroundColor: COMPARISON_COLORS[index % COMPARISON_COLORS.length] }}
                  />
                  <span
                    className="w-2 h-2 rounded-full"
                    style={{ backgroundColor: COMPARISON_COLORS[index % COMPARISON_COLORS.length] }}
                  />
                  <span
                    className="text-sm font-medium"
                    style={{ color: COMPARISON_COLORS[index % COMPARISON_COLORS.length] }}
                  >
                    {trend.name} ({trend.code})
                  </span>
                </div>
              ))}
            </div>

            {/* Stats Cards */}
            <div className="px-6 pb-6 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {comparisonTrends.map((trend, index) => {
                const latest = trend.data[trend.data.length - 1];
                return (
                  <div
                    key={trend.code}
                    className="p-4 rounded-xl border"
                    style={{ borderColor: `${COMPARISON_COLORS[index % COMPARISON_COLORS.length]}40` }}
                  >
                    <div className="flex items-center gap-2 mb-3">
                      <span
                        className="w-2 h-2 rounded-full"
                        style={{ backgroundColor: COMPARISON_COLORS[index % COMPARISON_COLORS.length] }}
                      />
                      <span className="font-medium text-gray-900 truncate" title={trend.name}>
                        {trend.name.length > 15 ? trend.name.substring(0, 15) + '...' : trend.name}
                      </span>
                    </div>
                    <div className="space-y-1 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-500">Min:</span>
                        <span className="font-medium">{trend.stats.min.toFixed(1)} {latest?.unit}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-500">Max:</span>
                        <span className="font-medium">{trend.stats.max.toFixed(1)} {latest?.unit}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-500">Media:</span>
                        <span className="font-medium">{trend.stats.avg.toFixed(1)} {latest?.unit}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-500">Ultimo:</span>
                        <span className="font-medium">{latest?.value.toFixed(1)} {latest?.unit}</span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// Trend Panel Component (shared between tabs)
function TrendPanel({
  selectedCode,
  trendData,
  loadingTrend,
  handleCopy,
  handleExport,
  formatDate,
}: {
  selectedCode: string | null;
  trendData: TrendData | null;
  loadingTrend: boolean;
  handleCopy: () => void;
  handleExport: () => void;
  formatDate: (date: string) => string;
}) {
  if (loadingTrend) {
    return (
      <div className="bg-white rounded-2xl border border-gray-200 p-8 flex items-center justify-center h-96">
        <p className="text-lg text-gray-500">Caricamento...</p>
      </div>
    );
  }

  if (!selectedCode || !trendData) {
    return (
      <div className="bg-white rounded-2xl border border-gray-200 p-8 flex items-center justify-center h-96">
        <div className="text-center">
          <svg className="w-16 h-16 mx-auto text-gray-300 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 12l3-3 3 3 4-4M8 21l4-4 4 4M3 4h18M4 4h16v12a1 1 0 01-1 1H5a1 1 0 01-1-1V4z" />
          </svg>
          <p className="text-lg text-gray-500">Seleziona un parametro per visualizzare il trend</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
      {/* Chart Header */}
      <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
        <div className="flex items-center gap-3">
          <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 12l3-3 3 3 4-4M8 21l4-4 4 4M3 4h18M4 4h16v12a1 1 0 01-1 1H5a1 1 0 01-1-1V4z" />
          </svg>
          <h2 className="text-xl font-bold text-gray-900">
            Trend {trendData.name}
          </h2>
        </div>
        <div className="flex gap-2">
          <button
            onClick={handleCopy}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            title="Copia negli appunti"
          >
            <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
            </svg>
          </button>
          <button
            onClick={handleExport}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            title="Esporta dati"
          >
            <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
            </svg>
          </button>
        </div>
      </div>

      {/* Parameter Info */}
      <div className="px-6 py-3 bg-gray-50 border-b border-gray-200">
        <p className="font-semibold text-gray-900">{trendData.name}</p>
        <p className="text-sm text-gray-500">{trendData.code}</p>
      </div>

      {/* Chart */}
      <div className="p-6">
        <TrendChart data={trendData} />
      </div>

      {/* Storico Valori Table */}
      <div className="border-t border-gray-200">
        <div className="px-6 py-3 bg-gray-50 border-b border-gray-200">
          <h3 className="font-semibold text-gray-700">Storico Valori</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-sm font-semibold text-gray-600">Data</th>
                <th className="px-6 py-3 text-left text-sm font-semibold text-gray-600">Valore</th>
                <th className="px-6 py-3 text-left text-sm font-semibold text-gray-600">Range</th>
                <th className="px-6 py-3 text-left text-sm font-semibold text-gray-600">Stato</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {[...trendData.data].reverse().map((point) => (
                <tr key={point.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 text-gray-900">{formatDate(point.testDate)}</td>
                  <td className="px-6 py-4">
                    <span className={point.isWithinRange ? 'text-gray-900' : 'text-red-600'}>
                      {point.value.toFixed(2)} {point.unit}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-gray-500">
                    {point.referenceMin ?? '-'} - {point.referenceMax ?? '-'}
                  </td>
                  <td className="px-6 py-4">
                    <ValueRangeBar
                      value={point.value}
                      min={point.referenceMin}
                      max={point.referenceMax}
                      isWithinRange={point.isWithinRange}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// Trend Chart Component
function TrendChart({ data }: { data: TrendData }) {
  if (data.data.length === 0) return null;

  const values = data.data.map(d => d.value);
  const minVal = Math.min(...values, data.referenceRange.min ?? Infinity);
  const maxVal = Math.max(...values, data.referenceRange.max ?? -Infinity);
  const range = maxVal - minVal || 1;
  const padding = range * 0.15;

  const chartMinY = minVal - padding;
  const chartMaxY = maxVal + padding;
  const chartRange = chartMaxY - chartMinY;

  const width = 600;
  const height = 200;
  const marginLeft = 50;
  const marginRight = 20;
  const marginTop = 20;
  const marginBottom = 30;

  const chartWidth = width - marginLeft - marginRight;
  const chartHeight = height - marginTop - marginBottom;

  const xScale = (i: number) => marginLeft + (i / (data.data.length - 1 || 1)) * chartWidth;
  const yScale = (v: number) => marginTop + chartHeight - ((v - chartMinY) / chartRange) * chartHeight;

  const linePath = data.data
    .map((d, i) => `${i === 0 ? 'M' : 'L'} ${xScale(i)} ${yScale(d.value)}`)
    .join(' ');

  const refMin = data.referenceRange.min;
  const refMax = data.referenceRange.max;

  return (
    <svg width="100%" height={height} viewBox={`0 0 ${width} ${height}`} className="overflow-visible">
      {/* Y axis */}
      <line x1={marginLeft} y1={marginTop} x2={marginLeft} y2={height - marginBottom} stroke="#e5e7eb" strokeWidth={1} />

      {/* Reference lines (dashed) */}
      {refMax !== null && (
        <>
          <line
            x1={marginLeft}
            y1={yScale(refMax)}
            x2={width - marginRight}
            y2={yScale(refMax)}
            stroke="#9ca3af"
            strokeDasharray="4 4"
            strokeWidth={1}
          />
          <text x={marginLeft - 5} y={yScale(refMax) + 4} textAnchor="end" fontSize={11} fill="#6b7280">
            Max: {refMax.toFixed(2)}
          </text>
        </>
      )}
      {refMin !== null && (
        <>
          <line
            x1={marginLeft}
            y1={yScale(refMin)}
            x2={width - marginRight}
            y2={yScale(refMin)}
            stroke="#9ca3af"
            strokeDasharray="4 4"
            strokeWidth={1}
          />
          <text x={marginLeft - 5} y={yScale(refMin) + 4} textAnchor="end" fontSize={11} fill="#6b7280">
            Min: {refMin.toFixed(2)}
          </text>
        </>
      )}

      {/* Data line */}
      <path d={linePath} fill="none" stroke="#3b82f6" strokeWidth={2} />

      {/* Data points */}
      {data.data.map((d, i) => (
        <circle
          key={d.id}
          cx={xScale(i)}
          cy={yScale(d.value)}
          r={5}
          fill={d.isWithinRange ? '#22c55e' : '#ef4444'}
          stroke="white"
          strokeWidth={2}
        />
      ))}

      {/* Y axis labels */}
      <text x={marginLeft - 5} y={marginTop + 4} textAnchor="end" fontSize={11} fill="#6b7280">
        {chartMaxY.toFixed(1)}
      </text>
      <text x={marginLeft - 5} y={height - marginBottom} textAnchor="end" fontSize={11} fill="#6b7280">
        {chartMinY.toFixed(1)}
      </text>

      {/* X axis labels */}
      {data.data.length > 0 && (
        <>
          <text x={marginLeft} y={height - 8} textAnchor="start" fontSize={11} fill="#6b7280">
            {formatDate(data.data[0].testDate)}
          </text>
          {data.data.length > 2 && (
            <text x={(marginLeft + width - marginRight) / 2} y={height - 8} textAnchor="middle" fontSize={11} fill="#6b7280">
              {formatDate(data.data[Math.floor(data.data.length / 2)].testDate)}
            </text>
          )}
          <text x={width - marginRight} y={height - 8} textAnchor="end" fontSize={11} fill="#6b7280">
            {formatDate(data.data[data.data.length - 1].testDate)}
          </text>
        </>
      )}
    </svg>
  );
}

// Comparison Chart Component - Multiple lines on same graph
function ComparisonChart({ trends, formatDate }: { trends: TrendData[]; formatDate: (date: string) => string }) {
  if (trends.length === 0) return null;

  // Collect all dates and normalize data by percentage for comparison
  // Since parameters have different units/scales, we normalize to percentage of their own range
  const allDates = new Set<string>();
  trends.forEach(trend => {
    trend.data.forEach(d => allDates.add(d.testDate));
  });
  const sortedDates = Array.from(allDates).sort();

  if (sortedDates.length === 0) return null;

  // For each trend, normalize values to 0-100 scale based on min/max
  const normalizedTrends = trends.map(trend => {
    const values = trend.data.map(d => d.value);
    const minVal = Math.min(...values);
    const maxVal = Math.max(...values);
    const range = maxVal - minVal || 1;

    return {
      ...trend,
      normalizedData: trend.data.map(d => ({
        ...d,
        normalizedValue: ((d.value - minVal) / range) * 100,
      })),
      minVal,
      maxVal,
    };
  });

  const width = 700;
  const height = 300;
  const marginLeft = 50;
  const marginRight = 20;
  const marginTop = 20;
  const marginBottom = 40;

  const chartWidth = width - marginLeft - marginRight;
  const chartHeight = height - marginTop - marginBottom;

  // X scale based on date index
  const xScale = (dateIndex: number) => marginLeft + (dateIndex / (sortedDates.length - 1 || 1)) * chartWidth;

  // Y scale 0-100 (normalized)
  const yScale = (normalizedValue: number) => marginTop + chartHeight - (normalizedValue / 100) * chartHeight;

  // Generate Y axis labels (show actual values for first trend as reference)
  const yLabels = [0, 25, 50, 75, 100];

  return (
    <svg width="100%" height={height} viewBox={`0 0 ${width} ${height}`} className="overflow-visible">
      {/* Grid lines */}
      {yLabels.map(label => (
        <line
          key={label}
          x1={marginLeft}
          y1={yScale(label)}
          x2={width - marginRight}
          y2={yScale(label)}
          stroke="#e5e7eb"
          strokeWidth={1}
        />
      ))}

      {/* Y axis */}
      <line x1={marginLeft} y1={marginTop} x2={marginLeft} y2={height - marginBottom} stroke="#d1d5db" strokeWidth={1} />

      {/* Y axis labels - show normalized scale */}
      {yLabels.map(label => (
        <text key={label} x={marginLeft - 8} y={yScale(label) + 4} textAnchor="end" fontSize={11} fill="#6b7280">
          {label}
        </text>
      ))}

      {/* Data lines for each trend */}
      {normalizedTrends.map((trend, trendIndex) => {
        // Create points array aligned to sortedDates
        const points: { x: number; y: number; value: number; date: string }[] = [];

        sortedDates.forEach((date, dateIndex) => {
          const dataPoint = trend.normalizedData.find(d => d.testDate === date);
          if (dataPoint) {
            points.push({
              x: xScale(dateIndex),
              y: yScale(dataPoint.normalizedValue),
              value: dataPoint.value,
              date,
            });
          }
        });

        if (points.length === 0) return null;

        const linePath = points
          .map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`)
          .join(' ');

        const color = COMPARISON_COLORS[trendIndex % COMPARISON_COLORS.length];

        return (
          <g key={trend.code}>
            {/* Line */}
            <path d={linePath} fill="none" stroke={color} strokeWidth={2} />

            {/* Points */}
            {points.map((p, i) => (
              <circle
                key={i}
                cx={p.x}
                cy={p.y}
                r={4}
                fill={color}
                stroke="white"
                strokeWidth={2}
              />
            ))}
          </g>
        );
      })}

      {/* X axis labels */}
      {sortedDates.length > 0 && (
        <>
          <text x={marginLeft} y={height - 10} textAnchor="start" fontSize={11} fill="#6b7280">
            {formatDate(sortedDates[0])}
          </text>
          {sortedDates.length > 2 && (
            <text x={width / 2} y={height - 10} textAnchor="middle" fontSize={11} fill="#6b7280">
              {formatDate(sortedDates[Math.floor(sortedDates.length / 2)])}
            </text>
          )}
          <text x={width - marginRight} y={height - 10} textAnchor="end" fontSize={11} fill="#6b7280">
            {formatDate(sortedDates[sortedDates.length - 1])}
          </text>
        </>
      )}
    </svg>
  );
}

// Italian Date Input Component
function ItalianDateInput({
  value,
  onChange,
  placeholder = 'gg/mm/aaaa',
}: {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
}) {
  const dateInputRef = useRef<HTMLInputElement>(null);

  // Convert ISO date to Italian format for display
  const isoToItalian = (iso: string) => {
    if (!iso) return '';
    const [year, month, day] = iso.split('-');
    return `${day}/${month}/${year}`;
  };

  // Convert Italian format to ISO for storage
  const italianToIso = (italian: string) => {
    const match = italian.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
    if (match) {
      const [, day, month, year] = match;
      return `${year}-${month}-${day}`;
    }
    return '';
  };

  const handleTextChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const input = e.target.value;
    // Allow typing with auto-formatting
    let formatted = input.replace(/[^\d/]/g, '');
    if (formatted.length === 2 && !formatted.includes('/')) {
      formatted += '/';
    } else if (formatted.length === 5 && formatted.split('/').length === 2) {
      formatted += '/';
    }
    if (formatted.length <= 10) {
      const iso = italianToIso(formatted);
      onChange(iso || '');
    }
  };

  const openDatePicker = () => {
    dateInputRef.current?.showPicker();
  };

  return (
    <div className="relative flex">
      <input
        type="text"
        value={isoToItalian(value)}
        onChange={handleTextChange}
        placeholder={placeholder}
        className="w-full px-3 py-2 pr-10 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
        maxLength={10}
      />
      <button
        type="button"
        onClick={openDatePicker}
        className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
      >
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
        </svg>
      </button>
      <input
        ref={dateInputRef}
        type="date"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="sr-only"
        tabIndex={-1}
      />
    </div>
  );
}

// Value Range Bar Component
function ValueRangeBar({ value, min, max, isWithinRange }: { value: number; min: number | null; max: number | null; isWithinRange: boolean }) {
  if (min === null || max === null) {
    return <span className="text-gray-400">-</span>;
  }

  const range = max - min;
  const extendedMin = min - range * 0.3;
  const extendedMax = max + range * 0.3;
  const totalRange = extendedMax - extendedMin;

  const normalizedValue = Math.max(0, Math.min(100, ((value - extendedMin) / totalRange) * 100));
  const normalizedMin = ((min - extendedMin) / totalRange) * 100;
  const normalizedMax = ((max - extendedMin) / totalRange) * 100;

  return (
    <div className="flex items-center gap-2">
      <span className={`text-sm font-medium ${isWithinRange ? 'text-green-600' : 'text-red-600'}`}>
        {value.toFixed(2)}
      </span>
      <div className="relative w-24 h-3 bg-gray-100 rounded-full overflow-hidden">
        {/* Normal range indicator */}
        <div
          className="absolute h-full bg-green-200"
          style={{
            left: `${normalizedMin}%`,
            width: `${normalizedMax - normalizedMin}%`,
          }}
        />
        {/* Value marker */}
        <div
          className={`absolute top-0 w-1 h-full ${isWithinRange ? 'bg-green-600' : 'bg-red-600'}`}
          style={{ left: `${normalizedValue}%` }}
        />
      </div>
    </div>
  );
}
